import { Download, PlayArrow, Settings } from '@mui/icons-material';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MarkItViewApi } from '../../../../Apis/Apis';
import {
  CalculateSeasonalMatrixResponse,
  CommodityType,
  MarketViewProductModel,
  MatrixContractSettlement,
  MatrixTerm,
  SeasonalMatrixStripType,
} from '../../../../Generated/Mark-It-View-Api';
import { CurrencyHelper } from '../../../../Helpers';
import { useLoadingState, useMtmDate } from '../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { StoneXLoading, StoneXRow } from '../../../StoneX';
import MarkItViewCommodityTypeSelector from '../../../StoneX/MarkItViewCommodityTypeSelector/MarkItViewCommodityTypeSelector';
import MarkItViewProductSelector from '../../../StoneX/MarkItViewProductSelector.tsx/MarkItViewProductSelector';
import { StoneXButton, StoneXSelect, StoneXToggleButton } from '../../../StoneXMui';
import { MatrixContractSettlementLabels, MatrixTermLabels } from '../Matrix/MatrixLabels';
import { MatrixTermResult, narrowResults } from '../Matrix/MatrixTypes';
import { exportSeasonalMatrixToPowerPoint } from './exportSeasonalMatrixToPowerPoint';
import { RollingContractNumberLabels, SeasonalMatrixStripTypeLabels, SeasonalMatrixTerms } from './SeasonalMatrixLabels';
import { ContractOption, SeasonalMatrixSelection, buildSeasonalMatrixRequest, getTargetLabel } from './SeasonalMatrixRequest';
import SeasonalMatrixResults from './SeasonalMatrixResults';
import { narrowVolatility } from './SeasonalMatrixTypes';
import { useUnexpiredContracts } from './useUnexpiredContracts';

const allStripTypes = Object.values(SeasonalMatrixStripType);
const allSettlements = Object.values(MatrixContractSettlement);
const rollingContractNumbers = [1, 2, 3];

// Pinned when the report runs so the chart and export always describe the plotted data, not later input changes.
interface SeasonalMatrixReportResult {
  response: CalculateSeasonalMatrixResponse;
  title: string;
  targetLabel: string;
  currencySymbol: string;
}

export default function SeasonalMatrixReport() {
  const quoteDate = useMtmDate();
  // useMtmDate returns a new Date on every render, so effects key off the formatted date instead.
  const quoteDateString = dayjs(quoteDate).format('YYYY-MM-DD');
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const isEmployee = userAccess.isEmployee;

  const currentYear = dayjs(quoteDate).year();
  const availableYears = Array.from({ length: 6 }, (_, i) => currentYear + i);

  const [selectedCommodityType, setSelectedCommodityType] = useState<CommodityType>(CommodityType.Favorites);
  const [selectedProduct, setSelectedProduct] = useState<MarketViewProductModel | null>(null);

  const [stripType, setStripType] = useState<SeasonalMatrixStripType>(SeasonalMatrixStripType.Strip);
  const [term, setTerm] = useState<MatrixTerm>(MatrixTerm.Annual);
  const [targetYear, setTargetYear] = useState<number>(currentYear + 1);
  const [targetContract, setTargetContract] = useState<ContractOption | null>(null);
  const [rollingContractNumber, setRollingContractNumber] = useState<number>(1);
  const [contractSettlement, setContractSettlement] = useState<MatrixContractSettlement>(MatrixContractSettlement.ExpirationDate);
  const [applyPpi, setApplyPpi] = useState<boolean>(true);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);
  const [averagesOnSecondAxis, setAveragesOnSecondAxis] = useState<boolean>(false);

  const [report, setReport] = useState<SeasonalMatrixReportResult | null>(null);
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const reportLoadingState = useLoadingState();

  // The Month choice only offers contracts that have not expired by the quote date, and starts on the nearest.
  const contractOptions = useUnexpiredContracts(selectedProduct, quoteDateString);
  useEffect(() => setTargetContract(contractOptions[0] ?? null), [contractOptions]);

  const matrix = useMemo<MatrixTermResult | null>(() => {
    if (!report?.response.matrix) {
      return null;
    }
    return { ...narrowResults([report.response.matrix])[0], season: report.title };
  }, [report]);

  const handleChartReady = useCallback((chart: Highcharts.Chart) => {
    chartRef.current = chart;
  }, []);

  function runReport() {
    if (!selectedProduct || !quoteDate) return;
    if (stripType === SeasonalMatrixStripType.Month && !targetContract) return;

    const selection: SeasonalMatrixSelection = {
      productId: selectedProduct.productId,
      quoteDate: quoteDateString,
      stripType: stripType,
      term: term,
      targetYear: targetYear,
      targetContract: targetContract,
      rollingContractNumber: rollingContractNumber,
      contractSettlement: contractSettlement,
      applyPpi: applyPpi,
    };
    const targetLabel = getTargetLabel(selection);

    MarkItViewApi.calculateSeasonalMatrix(reportLoadingState.setLoadingState, buildSeasonalMatrixRequest(selection))
      .then((response) => {
        setReport({
          response: response.data,
          title: `${selectedProduct.name ?? ''} ${targetLabel}`.trim(),
          targetLabel: targetLabel,
          currencySymbol: CurrencyHelper.getSymbol(selectedProduct.currency),
        });
      })
      .catch(() => setReport(null));
  }

  function exportToPowerPoint() {
    if (!report) return;
    exportSeasonalMatrixToPowerPoint({
      title: report.title,
      chart: chartRef.current,
      matrix: matrix,
      volatility: narrowVolatility(report.response.volatility),
      currencySymbol: report.currencySymbol,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Row 1: Product selection */}
      <StoneXRow>
        <MarkItViewCommodityTypeSelector
          value={selectedCommodityType}
          onChange={(x) => setSelectedCommodityType(x!)}
        />
        <MarkItViewProductSelector
          commodityType={selectedCommodityType}
          onProductChange={setSelectedProduct}
        />
      </StoneXRow>

      {/* Row 2: Strip type and the contracts it prices */}
      <StoneXRow align='start'>
        <StoneXSelect
          value={stripType}
          onChange={(value) => setStripType(value ?? SeasonalMatrixStripType.Strip)}
          options={allStripTypes}
          getOptionLabel={(x) => SeasonalMatrixStripTypeLabels[x]}
          getId={(x) => x}
          label="Strip Type"
        />
        {stripType === SeasonalMatrixStripType.Strip && (
          <>
            <StoneXSelect
              value={term}
              onChange={(value) => setTerm(value ?? MatrixTerm.Annual)}
              options={SeasonalMatrixTerms}
              getOptionLabel={(x) => MatrixTermLabels[x]}
              getId={(x) => x}
              label="Term"
            />
            <StoneXSelect
              value={targetYear}
              onChange={(value) => setTargetYear(value ?? currentYear + 1)}
              options={availableYears}
              getOptionLabel={(x) => x.toString()}
              getId={(x) => x.toString()}
              label="Year"
            />
          </>
        )}
        {stripType === SeasonalMatrixStripType.Month && (
          <StoneXSelect
            value={targetContract}
            onChange={setTargetContract}
            options={contractOptions}
            getOptionLabel={(x) => x.label}
            getId={(x) => x.label}
            label="Contract Month"
          />
        )}
        {stripType === SeasonalMatrixStripType.Rolling && (
          <StoneXSelect
            value={rollingContractNumber}
            onChange={(value) => setRollingContractNumber(value ?? 1)}
            options={rollingContractNumbers}
            getOptionLabel={(x) => RollingContractNumberLabels[x]}
            getId={(x) => x.toString()}
            label="Contract"
          />
        )}
      </StoneXRow>

      {/* Row 3: PPI, which only changes the Matrix panel, as on the Matrix screen */}
      <StoneXRow>
        <StoneXToggleButton label="Apply PPI to Matrix" value={applyPpi} onChange={setApplyPpi} />
      </StoneXRow>

      {/* Advanced Options toggle */}
      {isEmployee && (
        <StoneXRow>
          <StoneXButton onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} variant="text" startIcon={<Settings />}>
            {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
          </StoneXButton>
        </StoneXRow>
      )}

      {/* Advanced Options */}
      {showAdvancedOptions && (
        <StoneXRow>
          <StoneXSelect
            value={contractSettlement}
            onChange={(value) => setContractSettlement(value ?? MatrixContractSettlement.ExpirationDate)}
            options={allSettlements}
            getOptionLabel={(x) => MatrixContractSettlementLabels[x]}
            getId={(x) => x}
            label="Contract Settlement"
          />
        </StoneXRow>
      )}

      {/* Run button */}
      <StoneXRow>
        <StoneXButton
          onClick={runReport}
          disabled={!selectedProduct || (stripType === SeasonalMatrixStripType.Month && !targetContract) || reportLoadingState.isLoading()}
          startIcon={<PlayArrow />}
        >
          Run Seasonal Matrix
        </StoneXButton>
        {report && !reportLoadingState.isLoading() && (
          <StoneXButton onClick={exportToPowerPoint} variant="outlined" startIcon={<Download />}>
            Export to PowerPoint
          </StoneXButton>
        )}
      </StoneXRow>

      {/* Results. They unmount while a run loads, so each run starts with no bands pinned. */}
      <StoneXLoading show={reportLoadingState.isLoading()} />

      {report && !reportLoadingState.isLoading() && (
        <SeasonalMatrixResults
          response={report.response}
          matrix={matrix}
          title={report.title}
          targetLabel={report.targetLabel}
          currencySymbol={report.currencySymbol}
          averagesOnSecondAxis={averagesOnSecondAxis}
          onAveragesOnSecondAxisChange={setAveragesOnSecondAxis}
          onChartReady={handleChartReady}
        />
      )}
    </div>
  );
}
